import { useState, useEffect } from "react";
import { useOutletContext, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  Users, IndianRupee, Handshake, MessageSquare, Image, TrendingUp,
  ArrowRight, Calendar, MapPin, AlertTriangle, Eye
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { stats, getAuthAxios } = useOutletContext();
  const [recentRegistrations, setRecentRegistrations] = useState([]);
  const [failedRegistrations, setFailedRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const authAxios = getAuthAxios();
      const [dashRes, failedRes] = await Promise.all([
        authAxios.get(`${API}/admin/dashboard`),
        authAxios.get(`${API}/admin/registrations/failed`)
      ]);
      setRecentRegistrations(dashRes.data.recent_registrations || []);
      setFailedRegistrations(failedRes.data.failed_registrations || []);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      label: "Total Registrations", 
      value: stats?.total_registrations || 0, 
      icon: Users, 
      color: "bg-blue-500",
      link: "/admin/registrations"
    },
    { 
      label: "Total Revenue", 
      value: `₹${(stats?.total_revenue || 0).toLocaleString()}`, 
      icon: IndianRupee, 
      color: "bg-green-500",
      link: "/admin/registrations"
    },
    { 
      label: "Failed/Pending", 
      value: stats?.failed_registrations || failedRegistrations.length || 0, 
      icon: AlertTriangle, 
      color: "bg-red-500",
      link: "/admin/registrations"
    },
    { 
      label: "Website Visitors", 
      value: (stats?.total_visitors || 0).toLocaleString(), 
      icon: Eye, 
      color: "bg-cyan-500",
      link: "/admin"
    },
    { 
      label: "Sponsor Inquiries", 
      value: stats?.sponsor_inquiries || 0, 
      icon: Handshake, 
      color: "bg-purple-500",
      link: "/admin/sponsors"
    },
    { 
      label: "Paid Registrations", 
      value: stats?.paid_registrations || 0, 
      icon: TrendingUp, 
      color: "bg-emerald-500",
      link: "/admin/registrations"
    },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#0A0A0A] font-['Outfit']">Dashboard</h1>
        <p className="text-[#4B5563]">Welcome to The FinRun Admin Panel</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <Link key={card.label} to={card.link}>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                <CardContent className="p-4">
                  <div className={`w-10 h-10 ${card.color} rounded-xl flex items-center justify-center mb-3`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-2xl font-bold text-[#0A0A0A]">{card.value}</p>
                  <p className="text-xs text-[#4B5563]">{card.label}</p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Failed Registrations Alert */}
      {failedRegistrations.length > 0 && (
        <Card className="mb-8 border-red-200 bg-red-50">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-bold text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Failed/Pending Registrations ({failedRegistrations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-red-600 mb-4">
              These users started registration but payment was not completed. You may want to follow up.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left border-b border-red-200">
                    <th className="pb-2 text-sm font-medium text-red-700">Name</th>
                    <th className="pb-2 text-sm font-medium text-red-700">Email</th>
                    <th className="pb-2 text-sm font-medium text-red-700">Phone</th>
                    <th className="pb-2 text-sm font-medium text-red-700">Category</th>
                    <th className="pb-2 text-sm font-medium text-red-700">Amount</th>
                    <th className="pb-2 text-sm font-medium text-red-700">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {failedRegistrations.slice(0, 5).map((reg) => (
                    <tr key={reg.id} className="border-b border-red-100 last:border-0">
                      <td className="py-2 text-sm">{reg.first_name} {reg.last_name}</td>
                      <td className="py-2 text-sm text-gray-600">{reg.email}</td>
                      <td className="py-2 text-sm text-gray-600">{reg.phone}</td>
                      <td className="py-2">
                        <span className="px-2 py-1 bg-red-100 rounded text-xs font-medium text-red-700">
                          {reg.race_category}
                        </span>
                      </td>
                      <td className="py-2 text-sm font-medium">₹{(reg.amount / 100).toLocaleString()}</td>
                      <td className="py-2 text-sm text-gray-500">
                        {new Date(reg.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {failedRegistrations.length > 5 && (
              <p className="text-sm text-red-600 mt-3">
                + {failedRegistrations.length - 5} more failed registrations
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recent Registrations */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-bold">Recent Registrations</CardTitle>
          <Link to="/admin/registrations" className="text-sm text-[#007AFF] hover:underline flex items-center gap-1">
            View All <ArrowRight className="w-4 h-4" />
          </Link>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-[#4B5563]">Loading...</p>
          ) : recentRegistrations.length === 0 ? (
            <p className="text-[#4B5563]">No registrations yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left border-b">
                    <th className="pb-3 text-sm font-medium text-[#4B5563]">Name</th>
                    <th className="pb-3 text-sm font-medium text-[#4B5563]">Event</th>
                    <th className="pb-3 text-sm font-medium text-[#4B5563]">Category</th>
                    <th className="pb-3 text-sm font-medium text-[#4B5563]">Status</th>
                    <th className="pb-3 text-sm font-medium text-[#4B5563]">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRegistrations.map((reg) => (
                    <tr key={reg.id || reg.email} className="border-b last:border-0">
                      <td className="py-3">
                        <p className="font-medium text-[#0A0A0A]">{reg.first_name} {reg.last_name}</p>
                        <p className="text-xs text-[#4B5563]">{reg.email}</p>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-1 text-sm">
                          <MapPin className="w-3 h-3 text-[#FF3B30]" />
                          {reg.event === "chennai" ? "Chennai" : reg.event === "hyderabad-2027" ? "Hyderabad 2027" : "Hyderabad"}
                        </div>
                      </td>
                      <td className="py-3">
                        <span className="px-2 py-1 bg-[#FAFAFA] rounded-full text-xs font-medium">
                          {reg.race_category}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          reg.payment_status === "paid" 
                            ? "bg-green-100 text-green-700" 
                            : reg.payment_status === "failed"
                            ? "bg-red-100 text-red-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}>
                          {reg.payment_status === "paid" ? "Paid" : reg.payment_status === "failed" ? "Failed" : "Pending"}
                        </span>
                      </td>
                      <td className="py-3 text-sm font-medium">
                        ₹{(reg.amount / 100).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-4 mt-8">
        <Link to="/admin/gallery">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer bg-gradient-to-br from-pink-500 to-rose-500 text-white">
            <CardContent className="p-6">
              <Image className="w-8 h-8 mb-4" />
              <h3 className="text-lg font-bold mb-1">Manage Gallery</h3>
              <p className="text-sm text-white/80">Add photos & videos for past events</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/hero-video">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer bg-gradient-to-br from-blue-500 to-indigo-500 text-white">
            <CardContent className="p-6">
              <TrendingUp className="w-8 h-8 mb-4" />
              <h3 className="text-lg font-bold mb-1">Hero Video</h3>
              <p className="text-sm text-white/80">Set homepage background video</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/sponsors">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer bg-gradient-to-br from-purple-500 to-violet-500 text-white">
            <CardContent className="p-6">
              <Handshake className="w-8 h-8 mb-4" />
              <h3 className="text-lg font-bold mb-1">Sponsor Inquiries</h3>
              <p className="text-sm text-white/80">Review partnership requests</p>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboard;
